import api from './api';
import type { SegmentDefinitionPayload } from '@/types/chat';

export type { SegmentDefinitionPayload } from '@/types/chat';

// ============================================================================
// SEGMENTS API — reusable, versioned product groupings on a transaction
// account (the semantic layer). Agent-proposed, human-confirmed.
//
// Backend route base: /api/segments
// @see bizguard-backend/docs/SEMANTIC_ENTITIES_SEGMENTS_SPEC.md
// ============================================================================

export interface SegmentSummary {
  id: string;
  organization_id: string;
  data_source_account_id: string | null;
  key: string;
  name: string;
  current_version_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface SegmentVersion {
  id: string;
  entity_id: string;
  version_number: number;
  definition: SegmentDefinitionPayload;
  status: string;
  created_at: string;
}

export interface ResolvedSegmentPreview {
  segmentKey: string;
  versionId: string;
  fullWeightIds: string[];
  weightedIds: Array<{ id: string; weight: number }>;
  warnings: string[];
}

export interface CreateSegmentDto {
  data_source_account_id: string;
  key: string;
  name: string;
  definition: SegmentDefinitionPayload;
  change_note?: string;
}

export interface ProposeVersionDto {
  definition: SegmentDefinitionPayload;
  name?: string;
  change_note?: string;
}

export const segmentsApi = {
  async list(dataSourceAccountId: string): Promise<SegmentSummary[]> {
    const res = await api.get('/api/segments', {
      params: { data_source_account_id: dataSourceAccountId },
    });
    return res.data.data ?? [];
  },

  async get(segmentId: string): Promise<{ entity: SegmentSummary; currentVersion: SegmentVersion | null }> {
    const res = await api.get(`/api/segments/${segmentId}`);
    return res.data.data;
  },

  async preview(
    dataSourceAccountId: string,
    definition: SegmentDefinitionPayload,
  ): Promise<ResolvedSegmentPreview> {
    const res = await api.post('/api/segments/preview', {
      data_source_account_id: dataSourceAccountId,
      definition,
    });
    return res.data.data;
  },

  /** Create = propose + confirm server-side (the caller holds CONFIRM). */
  async create(dto: CreateSegmentDto): Promise<{ version: SegmentVersion }> {
    const res = await api.post('/api/segments', dto);
    return res.data.data;
  },

  async proposeVersion(segmentId: string, dto: ProposeVersionDto): Promise<{ version: SegmentVersion }> {
    const res = await api.post(`/api/segments/${segmentId}/versions`, dto);
    return res.data.data;
  },

  async confirmVersion(versionId: string): Promise<{ version: SegmentVersion }> {
    const res = await api.post(`/api/segments/versions/${versionId}/confirm`);
    return res.data.data;
  },

  /** Approve flow for an update card: propose a new draft, then confirm it. */
  async updateAndConfirm(segmentId: string, dto: ProposeVersionDto): Promise<{ version: SegmentVersion }> {
    const { version } = await this.proposeVersion(segmentId, dto);
    return this.confirmVersion(version.id);
  },

  async remove(segmentId: string): Promise<{ success: boolean }> {
    const res = await api.delete(`/api/segments/${segmentId}`);
    return { success: res.data.success };
  },
};

export default segmentsApi;
